import { WalletService, type WithdrawalFxQuote } from '@/services/wallet.service';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  computeWithdrawalFxFromReference,
  getTargetCurrency,
  isFxPairSupported,
  normalizeWalletCurrency,
  WITHDRAWAL_FX_REFERENCE_AMOUNT,
  type WalletCurrency,
} from '../utils/withdrawalConversion';

export function useWithdrawalFxConfig(enabled = true) {
  return useQuery({
    queryKey: ['withdrawal-fx-config'],
    queryFn: () => WalletService.getWithdrawalFxConfig(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

function useWithdrawalFxReferenceQuote({
  enabled,
  from,
  to,
}: {
  enabled: boolean;
  from: WalletCurrency;
  to: WalletCurrency;
}) {
  const amount = WITHDRAWAL_FX_REFERENCE_AMOUNT[from];

  return useQuery({
    queryKey: ['withdrawal-fx-reference-quote', from, to],
    queryFn: () =>
      WalletService.getWithdrawalFxQuote({
        amount_requested: amount,
        currency: from,
        payout_currency: to,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useWithdrawalFx({
  enabled = true,
  walletCurrency,
  isConverting,
  amountInCents,
}: {
  enabled?: boolean;
  walletCurrency: string;
  isConverting: boolean;
  amountInCents: number;
}) {
  const wallet = normalizeWalletCurrency(walletCurrency);
  const payoutCurrency = getTargetCurrency(wallet);

  const configQuery = useWithdrawalFxConfig(enabled);
  const supportedPairs = configQuery.data?.supported_pairs;

  const brlToUsdSupported = isFxPairSupported(supportedPairs, 'BRL', 'USD');
  const usdToBrlSupported = isFxPairSupported(supportedPairs, 'USD', 'BRL');

  const brlToUsdQuery = useWithdrawalFxReferenceQuote({
    enabled: enabled && brlToUsdSupported,
    from: 'BRL',
    to: 'USD',
  });

  const usdToBrlQuery = useWithdrawalFxReferenceQuote({
    enabled: enabled && usdToBrlSupported,
    from: 'USD',
    to: 'BRL',
  });

  const convertEnabled = isFxPairSupported(supportedPairs, wallet, payoutCurrency);

  const referenceQuote: WithdrawalFxQuote | undefined =
    wallet === 'BRL' ? brlToUsdQuery.data : usdToBrlQuery.data;

  const referenceQuery = wallet === 'BRL' ? brlToUsdQuery : usdToBrlQuery;

  const quote = useMemo(() => {
    if (!isConverting || !referenceQuote) return null;
    return computeWithdrawalFxFromReference(amountInCents, referenceQuote);
  }, [isConverting, referenceQuote, amountInCents]);

  const isReferenceLoading =
    convertEnabled && (configQuery.isLoading || referenceQuery.isLoading);

  const isReferenceError = convertEnabled && referenceQuery.isError;

  return {
    wallet,
    payoutCurrency,
    convertEnabled,
    config: configQuery.data,
    isConfigLoading: configQuery.isLoading,
    referenceQuote,
    quote,
    isQuoteLoading: isReferenceLoading,
    isQuoteError: isReferenceError,
  };
}
